import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { Button } from '../components/ui/button';
import { Shield, Users, FileText, Trash2, Search, ArrowLeft, ArrowRight, Loader2, BadgeCheck, ShieldOff, Download, Inbox, Mail, Phone, MessageSquare, X, Check, Clock, Send, Calendar, CreditCard, Briefcase, ClipboardList, Siren, PhoneCall, CalendarClock, KeyRound, UserCheck, UserX, Wallet, FileSignature, FolderOpen, ChevronUp, Network, ShieldCheck } from 'lucide-react';
import { MandantAktenPanel } from '../components/MandantAktenPanel';
import { PasswordDialog } from '../components/PasswordDialog';
import { AdminRueckrufe } from '../components/AdminRueckrufe';
import { AdminRegistrierungen } from '../components/AdminRegistrierungen';
import { AdminRaten } from '../components/AdminRaten';
import { AdminTermine } from '../components/AdminTermine';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useFeatures } from '../hooks/useFeatures';
import { Toaster } from '../components/ui/toaster';
import PdfDownloadButton from '../components/PdfDownloadButton';
import { ZuordnungChip } from '../components/register/ZuordnungChip';
import { useAdminUsers } from '../hooks/useAdminUsers';
import { BerichtEinstellungen } from '../components/BerichtEinstellungen';
import { ZugangBadge, ErneutEinladenBtn, SammelEinladenBtn, ZUGANG_FILTER } from '../components/ZugangStatus';
import { passtSuche } from '../lib/suche';
import { AnfragenListe } from '../components/kanzlei/AnfragenListe';
import { ErfassungDetailDialog } from '../components/kanzlei/ErfassungDetailDialog';

const SEITEN_TITEL = {
  anfragen: ['Alle Anfragen', 'Mandatsanfragen von der Website und Bewerbungen an einer Stelle. Ein Klick öffnet Details, Dateien und Status.'],
  termine: ['Termine', 'Termine mit Mandanten vorschlagen, bestätigen, verschieben und absagen.'],
  users: ['Portalkonten', 'Alle Mandantenkonten mit Portal-Zugang, Mandatsstatus und Einladungen.'],
  docs: ['Dokumente', 'Alle Dokumente aus Portal und Kanzlei an einer Stelle.'],
  raten: ['Ratenpläne', 'Ratenangebote und laufende Ratenpläne aller Mandanten.'],
  newsletter: ['Newsletter', 'Abonnenten und Versand des Kanzlei-Newsletters.'],
  rueckrufe: ['Rückrufe', 'Rückrufwünsche nach Zeitfenster.'],
  sepa: ['SEPA-Mandate', 'Erteilte Lastschriftmandate.'],
};

const Admin = ({ festerTab }) => {
  const features = useFeatures();
  const { user: currentUser, isAdmin } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(festerTab || searchParams.get('tab') || 'anfragen');
  const [rueckrufe, setRueckrufe] = useState([]);
  const [raten, setRaten] = useState({ items: [], stats: {} });
  const [pwUser, setPwUser] = useState(null);
  const [team, setTeam] = useState([]);
  const [termine, setTermine] = useState({ items: [], stats: {} });
  const [documents, setDocuments] = useState([]);
  const [anfragen, setAnfragen] = useState([]);
  const [selectedAnfrage, setSelectedAnfrage] = useState(null);
  const [erfassungId, setErfassungId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mandatFilter, setMandatFilter] = useState('alle');
  const [zugangFilter, setZugangFilter] = useState('alle');
  const { users, meta: usersMeta, laden: usersLaden, reload: reloadUsers, loadMore: mehrUsers, hatMehr: mehrUsersDa } = useAdminUsers({ search: tab === 'users' ? search : '', mandat: mandatFilter, zugang: zugangFilter });
  const [aktenUser, setAktenUser] = useState(null);

  // Newsletter-Digest state
  const [nlStatus, setNlStatus] = useState(null);
  const [nlSubscribers, setNlSubscribers] = useState([]);
  const [nlTestEmail, setNlTestEmail] = useState('');
  const [nlSending, setNlSending] = useState(false);

  // SEPA-Mandate state
  const [sepaMandates, setSepaMandates] = useState([]);
  const [sepaLoading, setSepaLoading] = useState(false);

  const fetchSepa = useCallback(async () => {
    setSepaLoading(true);
    try {
      const { data } = await api.get('/admin/sepa-mandate');
      setSepaMandates(data || []);
    } catch (e) {
      // silent
    } finally {
      setSepaLoading(false);
    }
  }, []);

  const updateSepaStatus = async (m, newStatus) => {
    try {
      const { data } = await api.patch(`/admin/sepa-mandate/${m.mandate_id}`, { status: newStatus });
      setSepaMandates((prev) => prev.map((x) => x.mandate_id === m.mandate_id ? { ...x, ...data } : x));
      toast({ title: 'Aktualisiert', description: `Status: ${newStatus}` });
    } catch (e) {
      toast({ title: 'Fehler', description: e?.response?.data?.detail || '—', variant: 'destructive' });
    }
  };

  const deleteSepa = async (m) => {
    if (!window.confirm(`Mandat von ${m.accountHolder} wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/sepa-mandate/${m.mandate_id}`);
      setSepaMandates((prev) => prev.filter((x) => x.mandate_id !== m.mandate_id));
      toast({ title: 'Gelöscht', description: m.mandate_id });
    } catch (e) {
      toast({ title: 'Fehler', description: 'Löschen fehlgeschlagen', variant: 'destructive' });
    }
  };

  const fetchNewsletter = useCallback(async () => {
    try {
      const [s, subs] = await Promise.all([
        api.get('/admin/newsletter/digest-status'),
        api.get('/admin/newsletter/subscribers'),
      ]);
      setNlStatus(s.data);
      setNlSubscribers(subs.data.items || []);
    } catch (e) {
      // silent — Tab vielleicht ohne Daten
    }
  }, []);

  const triggerDigest = async (mode) => {
    const testEmail = mode === 'test' ? nlTestEmail.trim() : null;
    if (mode === 'test' && !testEmail) {
      toast({ title: 'Fehler', description: 'Bitte Test-E-Mail eingeben.', variant: 'destructive' });
      return;
    }
    if (mode === 'broadcast') {
      const confirmedCount = nlStatus?.confirmed_subscribers ?? 0;
      if (!window.confirm(`Newsletter-Digest an ${confirmedCount} bestätigte Abonnenten senden?`)) return;
    }
    setNlSending(true);
    try {
      const url = mode === 'test'
        ? `/admin/newsletter/send-digest?test_email=${encodeURIComponent(testEmail)}`
        : '/admin/newsletter/send-digest';
      const { data } = await api.post(url);
      toast({
        title: 'Versand gestartet',
        description: data.mode === 'test'
          ? `Test-Mail an ${data.test_email} wird verschickt.`
          : 'Broadcast läuft im Hintergrund.',
      });
      // nach ~3s Status erneut laden
      setTimeout(fetchNewsletter, 3000);
    } catch (e) {
      toast({ title: 'Fehler', description: e.response?.data?.detail || 'Versand fehlgeschlagen', variant: 'destructive' });
    } finally {
      setNlSending(false);
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [d, a, rr, rp, tm, te] = await Promise.all([
        api.get('/admin/documents'),
        api.get('/admin/anfragen'),
        api.get('/admin/rueckrufe').catch(() => ({ data: { items: [] } })),
        api.get('/admin/raten').catch(() => ({ data: { items: [], stats: {} } })),
        api.get('/admin/team').catch(() => ({ data: [] })),
        api.get('/admin/termine').catch(() => ({ data: { items: [], stats: {} } })),
      ]);
      setDocuments(d.data);
      setAnfragen(a.data);
      setRueckrufe(rr.data.items || []);
      setRaten({ items: rp.data.items || [], stats: rp.data.stats || {} });
      setTeam(Array.isArray(tm.data) ? tm.data : (tm.data.items || []));
      setTermine(te.data);
    } catch (e) {
      toast({ title: 'Fehler', description: 'Daten konnten nicht geladen werden.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const tabParam = searchParams.get('tab');
  useEffect(() => { if (tabParam) setTab(tabParam); }, [tabParam]);

  useEffect(() => {
    if (tab === 'newsletter') fetchNewsletter();
    if (tab === 'sepa') fetchSepa();
  }, [tab, fetchNewsletter, fetchSepa]);

  const toggleAdmin = async (u) => {
    const newRole = u.role === 'admin' ? 'user' : 'admin';
    try {
      await api.patch(`/admin/users/${u.user_id}`, { role: newRole });
      toast({ title: 'Aktualisiert', description: `${u.name} ist jetzt ${newRole === 'admin' ? 'Administrator' : 'normaler Nutzer'}.` });
      reloadUsers();
    } catch (e) {
      toast({ title: 'Fehler', description: e.response?.data?.detail || 'Update fehlgeschlagen', variant: 'destructive' });
    }
  };

  const toggleMandat = async (u) => {
    const aktiv = u.mandat_aktiv === false;
    if (!aktiv && !window.confirm(`Mandat von ${u.name} (${u.email}) auf INAKTIV stellen? Der Mandant kann sich dann nicht mehr einloggen.`)) return;
    try {
      await api.patch(`/admin/users/${u.user_id}`, { mandat_aktiv: aktiv });
      toast({ title: aktiv ? 'Mandat aktiviert' : 'Mandat deaktiviert', description: `${u.name} – Login ${aktiv ? 'wieder möglich' : 'gesperrt'}.` });
      reloadUsers();
    } catch (e) {
      toast({ title: 'Fehler', description: e.response?.data?.detail || 'Update fehlgeschlagen', variant: 'destructive' });
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Wollen Sie ${u.name} (${u.email}) wirklich löschen? Alle Dokumente werden mitgelöscht.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast({ title: 'Gelöscht', description: u.email });
      reloadUsers();
      fetchAll();
    } catch (e) {
      toast({ title: 'Fehler', description: e.response?.data?.detail || 'Löschen fehlgeschlagen', variant: 'destructive' });
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.doc_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Fehler', description: 'Download fehlgeschlagen', variant: 'destructive' });
    }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`"${doc.filename}" wirklich löschen?`)) return;
    try {
      await api.delete(`/documents/${doc.doc_id}`);
      toast({ title: 'Gelöscht', description: doc.filename });
      fetchAll();
    } catch (e) {
      toast({ title: 'Fehler', description: 'Löschen fehlgeschlagen', variant: 'destructive' });
    }
  };

  const STATUS_BADGES = {
    neu: { label: 'Neu', cls: 'bg-rose-100 text-rose-700' },
    in_bearbeitung: { label: 'In Bearbeitung', cls: 'bg-amber-100 text-amber-700' },
    kontaktiert: { label: 'Kontaktiert', cls: 'bg-sky-100 text-sky-700' },
    abgeschlossen: { label: 'Abgeschlossen', cls: 'bg-emerald-100 text-emerald-700' },
    abgelehnt: { label: 'Abgelehnt', cls: 'bg-gray-200 text-gray-700' },
  };

  const updateAnfrageStatus = async (a, newStatus) => {
    try {
      const { data } = await api.patch(`/admin/anfragen/${a.anfrage_id}`, { status: newStatus });
      setAnfragen((prev) => prev.map((x) => (x.anfrage_id === a.anfrage_id ? data : x)));
      if (selectedAnfrage?.anfrage_id === a.anfrage_id) setSelectedAnfrage(data);
      toast({ title: 'Aktualisiert', description: `Status: ${STATUS_BADGES[newStatus]?.label || newStatus}` });
    } catch (e) {
      toast({ title: 'Fehler', description: e.response?.data?.detail || 'Update fehlgeschlagen', variant: 'destructive' });
    }
  };

  const deleteAnfrage = async (a) => {
    if (!window.confirm(`Anfrage von ${a.firstName} ${a.lastName} wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/anfragen/${a.anfrage_id}`);
      setAnfragen((prev) => prev.filter((x) => x.anfrage_id !== a.anfrage_id));
      if (selectedAnfrage?.anfrage_id === a.anfrage_id) setSelectedAnfrage(null);
      toast({ title: 'Gelöscht', description: a.anfrage_id });
    } catch (e) {
      toast({ title: 'Fehler', description: 'Löschen fehlgeschlagen', variant: 'destructive' });
    }
  };

  const filteredUsers = users;
  const filteredDocs = documents.filter((d) => passtSuche(search, d, [d.filename, d.owner_email, d.zuordnung?.aktenzeichen, d.aktenzeichen]));
  const newAnfragenCount = anfragen.filter((a) => a.status === 'neu').length;

  const formatSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className={festerTab ? 'min-h-screen bg-[#f5f8fa] bg-[radial-gradient(circle_at_1px_1px,rgba(27,58,92,0.07)_1px,transparent_0)] [background-size:22px_22px]' : 'min-h-screen bg-white'}>
      <Header />
      <main className={festerTab ? 'pt-[104px] lg:pt-[152px] pb-20 min-h-[80vh]' : 'pt-[150px] pb-20 bg-[#f5f8fa] min-h-[80vh]'}>
        {!festerTab && <section className="bg-[#1B3A5C] text-white py-10 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#b8893d] rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
            <Link to="/dashboard" className="inline-flex items-center gap-1 text-white/70 text-sm hover:text-[#b8893d] mb-3">
              <ArrowLeft className="w-3.5 h-3.5" /> Zum Dashboard
            </Link>
            <div className="flex items-center gap-3">
              <Shield className="w-7 h-7 text-[#b8893d]" />
              <h1 className="font-serif text-3xl md:text-4xl">Admin-Bereich</h1>
            </div>
            <p className="text-white/70 text-sm mt-2">Verwaltung von Mandanten und Dokumenten</p>
          </div>
        </section>}

        <section className="py-10">
          <div className={festerTab ? 'max-w-[1500px] mx-auto px-4 lg:px-8' : 'max-w-6xl mx-auto px-6 lg:px-10'}>
            {!festerTab && <AdminRegistrierungen />}
            {/* Tageszeile */}
            {!festerTab && (() => {
              const heute = new Date().toDateString();
              const istHeute = (iso) => iso && new Date(iso).toDateString() === heute;
              const offeneRueckrufe = rueckrufe.filter((r) => r.status === 'offen').length;
              const uploadsHeute = documents.filter((d) => istHeute(d.created_at) && d.uploaded_by === 'mandant').length;
              return (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-8 px-5 py-3 bg-[#1B3A5C] text-white rounded-sm text-sm" data-testid="admin-today-bar">
                  <span className="text-[10px] tracking-[0.25em] text-[#e0b56a] uppercase">Heute offen</span>
                  <button type="button" onClick={() => setTab('rueckrufe')} className="hover:text-[#e0b56a]" data-testid="today-rueckrufe"><strong className="font-serif text-xl mr-1">{offeneRueckrufe}</strong> Rückrufe</button>
                  <span className="text-white/30">·</span>
                  <button type="button" onClick={() => setTab('anfragen')} className="hover:text-[#e0b56a]" data-testid="today-anfragen"><strong className="font-serif text-xl mr-1">{newAnfragenCount}</strong> neue Anfragen</button>
                  <span className="text-white/30">·</span>
                  <button type="button" onClick={() => setTab('docs')} className="hover:text-[#e0b56a]" data-testid="today-uploads"><strong className="font-serif text-xl mr-1">{uploadsHeute}</strong> Uploads heute</button>
                </div>
              );
            })()}

            {/* Übersicht – zwei Gruppen mit einheitlichen Karten */}
            {!festerTab && (() => {
              const offeneRueckrufe = rueckrufe.filter((r) => r.status === 'offen').length;
              const gruppen = [
                { titel: 'Posteingang', desc: 'Was von außen hereinkommt', tiles: [
                  { key: 'anfragen', icon: Inbox, label: 'Anfragen', value: anfragen.length, badge: newAnfragenCount || null, hint: 'Schnellliste', onClick: () => setTab('anfragen') },
                  { key: 'erfassungen', icon: ClipboardList, label: 'Erfassungen', value: anfragen.length, hint: 'Detail + Dateien', external: '/admin/uploads' },
                  { key: 'rueckrufe', icon: PhoneCall, label: 'Rückrufe', value: rueckrufe.length, badge: offeneRueckrufe || null, hint: `${offeneRueckrufe} offen`, onClick: () => setTab('rueckrufe') },
                  { key: 'bewerbungen', icon: Briefcase, label: 'Bewerbungen', value: null, hint: 'Karriere', external: '/admin/bewerbungen' },
                  { key: 'notfall', icon: Siren, label: 'Notfall', value: null, hint: 'Hotline-Kontakte', external: '/admin/notfallkontakte', feature: 'notfall' },
                ] },
                { titel: 'Verwaltung', desc: 'Stammdaten & laufende Verträge', tiles: [
                  { key: 'm360', icon: Network, label: 'Mandanten-360°', value: null, hint: 'Register · Zuordnung aller Bereiche', external: '/kanzlei/mandanten' },
                  { key: 'posten', icon: Wallet, label: 'Offene Posten', value: null, hint: 'Offene & überfällige Beträge aller Mandanten', external: '/kanzlei/mandanten/posten' },
                  { key: 'rechtstexte', icon: ShieldCheck, label: 'Rechtstexte', value: null, hint: 'AGB & Datenschutz – Bestätigungen', external: '/admin/rechtstexte' },
                  { key: 'users', icon: Users, label: 'Mandanten', value: usersMeta.gesamt, onClick: () => setTab('users') },
                  { key: 'docs', icon: FileText, label: 'Dokumente', value: documents.length, onClick: () => setTab('docs') },
                  { key: 'sepa', icon: CreditCard, label: 'SEPA', value: sepaMandates.length || '—', hint: 'Lastschriftmandate', onClick: () => setTab('sepa') },
                  { key: 'termine', icon: Calendar, label: 'Termine', value: termine.stats?.kommend ?? '—', badge: termine.stats?.heute || null, hint: `${termine.stats?.heute || 0} heute · Bestätigung per Mail`, onClick: () => setTab('termine') },
                  { key: 'raten', icon: CalendarClock, label: 'Ratenpläne', value: raten.items.length || '—', badge: ((raten.stats.angefragt || 0) + (raten.stats.ueberfaellig || 0)) || null, hint: `${raten.stats.aktiv || 0} aktiv · ${raten.stats.ueberfaellig || 0} überfällig`, onClick: () => setTab('raten') },
                  { key: 'newsletter', icon: Mail, label: 'Newsletter', value: nlStatus?.confirmed_subscribers ?? '—', hint: 'Abonnenten', onClick: () => setTab('newsletter') },
                  { key: 'akonto', icon: Wallet, label: 'Akontozahlungen', value: null, hint: 'Abschlagszahlungen · Stripe', external: '/admin/akonto' },
                  { key: 'esign', icon: FileSignature, label: 'Mandatsunterlagen', value: null, hint: 'Vollmacht & Vergütungsvereinbarung · E-Signatur', external: '/admin/mandatsunterlagen' },
                  { key: 'zahlungen', icon: CreditCard, label: 'Zahlungen', value: null, hint: 'Notfallpauschale', external: '/admin/zahlungen', feature: 'notfall' },
                ] },
              ];
              const Card = ({ t }) => {
                const active = !t.external && tab === t.key;
                const cls = `relative flex items-center gap-3 text-left p-4 rounded-sm border transition-all ${active ? 'bg-[#1B3A5C] border-[#1B3A5C] text-white shadow-md' : 'bg-white border-gray-200 hover:border-[#b8893d] hover:shadow-sm text-[#1B3A5C]'}`;
                const inner = (
                  <>
                    {t.badge != null && <span className="absolute -top-2 -right-2 bg-rose-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center" data-testid={`tile-${t.key}-badge`}>{t.badge}</span>}
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${active ? 'bg-white/15 text-white' : 'bg-[#f5f8fa] text-[#b8893d]'}`}><t.icon className="w-5 h-5" /></span>
                    <span className="min-w-0">
                      <span className={`block text-[10px] tracking-[0.18em] uppercase ${active ? 'text-white/80' : 'text-[#34536b]'}`}>{t.label}</span>
                      <span className="flex items-baseline gap-2">
                        {t.value != null && <span className="font-serif text-xl leading-none">{t.value}</span>}
                        <span className={`text-[10px] ${active ? 'text-white/70' : 'text-[#7a8a99]'}`}>{t.hint || (t.external ? 'Öffnen' : '')}</span>
                      </span>
                    </span>
                    {t.external && <ArrowRight className={`w-4 h-4 ml-auto flex-shrink-0 ${active ? 'text-white' : 'text-[#b8893d]'}`} />}
                  </>
                );
                return t.external
                  ? <Link to={t.external} data-testid={`tile-${t.key}`} className={cls}>{inner}</Link>
                  : <button type="button" onClick={t.onClick} data-testid={`tile-${t.key}`} className={cls}>{inner}</button>;
              };
              return (
                <div className="space-y-6 mb-10" data-testid="admin-tiles">
                  {gruppen.map((g) => (
                    <div key={g.titel} data-testid={`admin-gruppe-${g.titel.toLowerCase()}`}>
                      <div className="flex items-baseline gap-2 mb-3"><span className="text-xs tracking-[0.2em] uppercase font-medium text-[#1B3A5C]">{g.titel}</span><span className="text-[11px] text-[#9aa5b1]">{g.desc}</span></div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                        {g.tiles.filter((t) => !t.feature || features[t.feature]).map((t) => <Card key={t.key} t={t} />)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Aktiver Bereich – Titelzeile */}
            {festerTab ? (
              <div className="mb-6">
                <h1 className="font-serif text-4xl sm:text-5xl text-[#1B3A5C]" data-testid="active-section-title">{SEITEN_TITEL[festerTab]?.[0] || 'Kanzlei'}</h1>
                {SEITEN_TITEL[festerTab]?.[1] && <p className="text-sm text-[#34536b] mt-2 max-w-2xl">{SEITEN_TITEL[festerTab][1]}</p>}
              </div>
            ) : <div className="flex items-center justify-between mb-4 px-1">
              <div className="inline-flex items-center gap-2 text-[#1B3A5C]">
                <div className="w-1 h-6 bg-[#b8893d]"></div>
                <h2 className="font-serif text-xl tracking-wide" data-testid="active-section-title">
                  {tab === 'anfragen' ? `Anfragen (${anfragen.length})` :
                   tab === 'rueckrufe' ? `Rückrufe (${rueckrufe.filter((r) => r.status === 'offen').length} offen)` :
                   tab === 'users' ? `Mandanten (${usersMeta.gesamt})` :
                   tab === 'docs' ? `Dokumente (${documents.length})` :
                   tab === 'newsletter' ? 'Newsletter-Verwaltung' :
                   tab === 'sepa' ? `SEPA-Mandate (${sepaMandates.length})` :
                   tab === 'raten' ? `Ratenpläne (${raten.items.length})` :
                   tab === 'termine' ? `Termine (${termine.items.length})` : ''}
                </h2>
              </div>
            </div>}

            {tab !== 'anfragen' && <div className="relative mb-5">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#34536b]" />
              <input
                placeholder="Suchen: Mandanten-Nr., Name, E-Mail, AZ …"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="admin-search-input"
                className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm"
              />
            </div>}

            {loading ? (
              <div className="text-center py-12"><Loader2 className="w-8 h-8 text-[#b8893d] animate-spin mx-auto" /></div>
            ) : tab === 'anfragen' ? (
              <AnfragenListe anfragen={anfragen} suche={search} setSuche={setSearch} passtSuche={passtSuche} onOpen={setSelectedAnfrage} onDelete={isAdmin ? deleteAnfrage : null} toast={toast} onReload={() => api.get('/admin/anfragen').then((r) => setAnfragen(r.data)).catch(() => {})} />
            ) : tab === 'rueckrufe' ? (
              <AdminRueckrufe items={rueckrufe} api={api} onChanged={fetchAll} toast={toast} search={search} team={team} />
            ) : tab === 'termine' ? (
              <AdminTermine data={termine} api={api} onChanged={fetchAll} toast={toast} team={team} search={search} canDelete={isAdmin} />
            ) : tab === 'raten' ? (
              <AdminRaten items={raten.items} stats={raten.stats} api={api} onChanged={fetchAll} toast={toast} search={search} canDelete={isAdmin} />
            ) : tab === 'users' ? (
              <div className="bg-white rounded-sm shadow-sm overflow-hidden" data-testid="admin-users-table">
                {isAdmin && <BerichtEinstellungen toast={toast} />}
                <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-gray-100" data-testid="mandat-filter">
                  {[
                    { key: 'alle', label: `Alle (${usersMeta.counts.mandat.alle ?? 0})` },
                    { key: 'aktiv', label: `Aktiv (${usersMeta.counts.mandat.aktiv ?? 0})` },
                    { key: 'inaktiv', label: `Inaktiv (${usersMeta.counts.mandat.inaktiv ?? 0})` },
                  ].map((f) => (
                    <button key={f.key} type="button" onClick={() => setMandatFilter(f.key)} data-testid={`mandat-filter-${f.key}`}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${mandatFilter === f.key ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'bg-white text-[#34536b] border-gray-200 hover:border-[#b8893d]'}`}>
                      {f.label}
                    </button>
                  ))}
                  <span className="hidden sm:inline w-px h-5 bg-gray-200 mx-1" />
                  <span className="text-[10px] tracking-[0.2em] text-[#7a8a99] uppercase">Portal-Zugang</span>
                  {ZUGANG_FILTER.map((f) => (
                    <button key={f.key} type="button" onClick={() => setZugangFilter(f.key)} data-testid={`zugang-filter-${f.key}`}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${zugangFilter === f.key ? 'bg-[#b8893d] text-white border-[#b8893d]' : 'bg-white text-[#34536b] border-gray-200 hover:border-[#b8893d]'}`}>
                      {f.label} ({usersMeta.counts.zugang[f.key] ?? 0})
                    </button>
                  ))}
                  <SammelEinladenBtn anzahl={usersMeta.abgelaufen_gesamt} toast={toast} onDone={reloadUsers} />
                </div>
                <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#f5f8fa] text-xs tracking-wider text-[#34536b] font-semibold">
                  <div className="col-span-3">NAME</div>
                  <div className="col-span-3">E-MAIL</div>
                  <div className="col-span-2">MANDAT</div>
                  <div className="col-span-2">REGISTRIERT</div>
                  <div className="col-span-2 text-right">AKTIONEN</div>
                </div>
                {filteredUsers.map((u, i) => {
                  const isTeam = u.role !== 'user';
                  const inaktiv = u.mandat_aktiv === false;
                  const aktenOffen = aktenUser === u.user_id;
                  return (
                  <React.Fragment key={u.user_id}>
                  <div className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 items-center hover:bg-[#fafaf7] ${i > 0 ? 'border-t border-gray-100' : ''} ${inaktiv ? 'opacity-70' : ''} ${aktenOffen ? 'bg-[#fbf7f0]' : ''}`} data-testid={`user-row-${u.user_id}`}>
                    <div className="col-span-3 flex items-center gap-2 min-w-0">
                      <span className="font-medium text-[#1B3A5C] truncate">{u.name}</span>
                      {u.role === 'admin' && <BadgeCheck className="w-4 h-4 text-[#b8893d] flex-shrink-0" />}
                    </div>
                    <div className="col-span-3 text-sm text-[#34536b] truncate">
                      <div className="truncate">{u.email}</div>
                      <div className="text-[10px] text-[#7a8a99]">{u.mandanten_nr && <span className="font-mono text-[#1B3A5C] mr-2" data-testid={`user-mnr-${u.user_id}`}>M {u.mandanten_nr}</span>}{u.auth_provider === 'google' ? 'Google' : 'E-Mail/PW'}</div>
                    </div>
                    <div className="col-span-2">
                      {isTeam ? (
                        <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-sm bg-[#f5f8fa] text-[#34536b]" data-testid={`mandat-status-${u.user_id}`}>Team</span>
                      ) : (
                        <span className={`inline-flex items-center text-xs px-2.5 py-1 rounded-sm ${inaktiv ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`} data-testid={`mandat-status-${u.user_id}`}>
                          {inaktiv ? 'Inaktiv' : 'Aktiv'}
                        </span>
                      )}
                      <div><ZugangBadge u={u} toast={toast} onDone={reloadUsers} /></div>
                    </div>
                    <div className="col-span-2 text-xs text-[#34536b]">{new Date(u.created_at).toLocaleDateString('de-DE')}</div>
                    <div className="col-span-2 flex justify-end gap-1">
                      <ErneutEinladenBtn u={u} toast={toast} onDone={reloadUsers} />
                      {!isTeam && (
                        <button
                          onClick={() => setAktenUser(aktenOffen ? null : u.user_id)}
                          className={`w-8 h-8 flex items-center justify-center rounded-sm ${aktenOffen ? 'bg-[#1B3A5C] text-white' : 'text-[#1B3A5C] hover:bg-[#f5f8fa]'}`}
                          title="Akten & Chats verwalten (Aktenzeichen, Angelegenheit, archivieren / reaktivieren, Chat sperren)"
                          data-testid={`user-akten-${u.user_id}`}
                          aria-expanded={aktenOffen}
                        >
                          {aktenOffen ? <ChevronUp className="w-4 h-4" /> : <FolderOpen className="w-4 h-4" />}
                        </button>
                      )}
                      {!isTeam && (
                        <button
                          onClick={() => toggleMandat(u)}
                          className={`w-8 h-8 flex items-center justify-center rounded-sm ${inaktiv ? 'text-emerald-700 hover:bg-emerald-50' : 'text-amber-700 hover:bg-amber-50'}`}
                          title={inaktiv ? 'Mandat aktivieren (Login freischalten)' : 'Mandat deaktivieren (Login sperren)'}
                          data-testid={`mandat-toggle-${u.user_id}`}
                        >
                          {inaktiv ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                        </button>
                      )}
                      {isAdmin && <button
                        onClick={() => setPwUser(u)}
                        className="w-8 h-8 flex items-center justify-center text-[#1B3A5C] hover:bg-[#f5f8fa] rounded-sm"
                        title="Passwort setzen / zurücksetzen"
                        data-testid={`user-password-${u.user_id}`}
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>}
                      {isAdmin && u.user_id !== currentUser?.user_id && (
                        <>
                          <button
                            onClick={() => toggleAdmin(u)}
                            className="w-8 h-8 flex items-center justify-center text-[#1B3A5C] hover:bg-[#f5f8fa] rounded-sm"
                            title={u.role === 'admin' ? 'Admin-Rechte entziehen' : 'Zum Admin machen'}
                          >
                            {u.role === 'admin' ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => deleteUser(u)}
                            className="w-8 h-8 flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded-sm"
                            title="Löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {aktenOffen && <MandantAktenPanel email={u.email} name={u.name} isAdmin={isAdmin} />}
                  </React.Fragment>
                  );
                })}
                {filteredUsers.length === 0 && !usersLaden && <p className="text-center py-10 text-[#34536b] text-sm" data-testid="users-empty">Keine Mandanten gefunden</p>}
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 text-xs text-[#7a8a99]" data-testid="users-pagination">
                  <span data-testid="users-count">{filteredUsers.length} von {usersMeta.total} angezeigt{usersMeta.total !== usersMeta.gesamt ? ` (gesamt ${usersMeta.gesamt} Konten)` : ''}</span>
                  {mehrUsersDa && (
                    <button type="button" onClick={mehrUsers} disabled={usersLaden} className="px-4 py-2 border border-[#1B3A5C] text-[#1B3A5C] rounded-sm hover:bg-[#1B3A5C] hover:text-white transition-colors disabled:opacity-50" data-testid="users-load-more">
                      {usersLaden ? 'Lädt …' : `Mehr laden (${Math.min(50, usersMeta.total - filteredUsers.length)})`}
                    </button>
                  )}
                </div>
              </div>
            ) : tab === 'newsletter' ? (
              <div className="space-y-6" data-testid="admin-newsletter-panel">
                {/* Status + Trigger Card */}
                <div className="bg-white rounded-sm shadow-sm p-6">
                  <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                    <div>
                      <h3 className="font-serif text-xl text-[#1B3A5C] mb-1">Wöchentlicher Digest</h3>
                      <p className="text-xs text-[#34536b]">
                        Automatischer Versand: <strong>Freitag 16:00 Uhr (Europe/Berlin)</strong> ·
                        Top 5 News-Artikel der letzten 7 Tage.
                      </p>
                    </div>
                    <div className="flex gap-3 text-center">
                      <div className="bg-[#f5f8fa] px-4 py-2 rounded-sm">
                        <div className="text-[10px] tracking-wider text-[#34536b]">BESTÄTIGT</div>
                        <div className="font-serif text-2xl text-[#1B3A5C]" data-testid="nl-confirmed-count">
                          {nlStatus?.confirmed_subscribers ?? '—'}
                        </div>
                      </div>
                      <div className="bg-[#f5f8fa] px-4 py-2 rounded-sm">
                        <div className="text-[10px] tracking-wider text-[#34536b]">GESAMT</div>
                        <div className="font-serif text-2xl text-[#1B3A5C]">{nlSubscribers.length}</div>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 mt-4">
                    {/* Test-Versand */}
                    <div className="border border-gray-200 rounded-sm p-4">
                      <div className="text-xs tracking-wider text-[#b8893d] mb-2 inline-flex items-center gap-1.5">
                        <Send className="w-3.5 h-3.5" /> TEST-VERSAND
                      </div>
                      <p className="text-xs text-[#34536b] mb-3">
                        Schickt den aktuellen Digest an eine einzelne Test-Adresse — ideal,
                        um Layout und Artikelauswahl zu prüfen.
                      </p>
                      <input
                        type="email"
                        placeholder="test@example.com"
                        value={nlTestEmail}
                        onChange={(e) => setNlTestEmail(e.target.value)}
                        data-testid="nl-test-email-input"
                        className="w-full px-3 py-2 border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm mb-3"
                      />
                      <button
                        onClick={() => triggerDigest('test')}
                        disabled={nlSending || !nlTestEmail.trim()}
                        data-testid="nl-send-test-btn"
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#1B3A5C] hover:bg-[#244c75] text-white text-xs tracking-wider py-2.5 rounded-sm transition-colors disabled:opacity-50"
                      >
                        {nlSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        TEST-MAIL SENDEN
                      </button>
                    </div>

                    {/* Broadcast */}
                    <div className="border border-[#b8893d] bg-[#fbf7f0] rounded-sm p-4">
                      <div className="text-xs tracking-wider text-[#b8893d] mb-2 inline-flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" /> JETZT AN ALLE SENDEN
                      </div>
                      <p className="text-xs text-[#34536b] mb-3">
                        Sendet den Digest sofort an <strong>{nlStatus?.confirmed_subscribers ?? 0}</strong> bestätigte Abonnenten.
                        Vorsicht — keine Rückgängigmachung möglich.
                      </p>
                      <button
                        onClick={() => triggerDigest('broadcast')}
                        disabled={nlSending || !nlStatus?.confirmed_subscribers}
                        data-testid="nl-send-broadcast-btn"
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#b8893d] hover:bg-[#a07730] text-white text-xs tracking-wider py-2.5 rounded-sm transition-colors disabled:opacity-50"
                      >
                        {nlSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                        BROADCAST AUSLÖSEN
                      </button>
                    </div>
                  </div>
                </div>

                {/* Letzter Lauf */}
                {nlStatus?.last_run && (
                  <div className="bg-white rounded-sm shadow-sm p-6" data-testid="nl-last-run">
                    <h4 className="font-serif text-lg text-[#1B3A5C] mb-4 inline-flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#b8893d]" /> Letzter Versand
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-[10px] tracking-wider text-[#34536b]">AUSGELÖST DURCH</div>
                        <div className="text-[#1B3A5C] capitalize">{nlStatus.last_run.triggered_by}</div>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-wider text-[#34536b]">STATUS</div>
                        <div className={`font-medium ${nlStatus.last_run.status === 'ok' ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {nlStatus.last_run.status}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-wider text-[#34536b]">GESENDET / FEHLER</div>
                        <div className="text-[#1B3A5C]">{nlStatus.last_run.sent} / {nlStatus.last_run.failed}</div>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-wider text-[#34536b]">ZEITPUNKT</div>
                        <div className="text-[#1B3A5C] text-xs">
                          {new Date(nlStatus.last_run.started_at).toLocaleString('de-DE')}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Abonnenten-Liste */}
                <div className="bg-white rounded-sm shadow-sm overflow-hidden" data-testid="nl-subscribers-list">
                  <div className="px-6 py-3 bg-[#f5f8fa] flex items-center justify-between">
                    <h4 className="font-serif text-sm text-[#1B3A5C] tracking-wider">ABONNENTEN</h4>
                    <span className="text-xs text-[#34536b]">{nlSubscribers.length} insgesamt</span>
                  </div>
                  <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#f5f8fa] text-xs tracking-wider text-[#34536b] font-semibold border-t border-gray-100">
                    <div className="col-span-5">E-MAIL</div>
                    <div className="col-span-2">STATUS</div>
                    <div className="col-span-3">RECHTSGEBIETE</div>
                    <div className="col-span-2">ANGEMELDET</div>
                  </div>
                  {nlSubscribers.map((s, i) => (
                    <div key={s.id} className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-3 items-center hover:bg-[#fafaf7] ${i > 0 ? 'border-t border-gray-100' : ''}`}>
                      <div className="col-span-5 text-sm text-[#1B3A5C] truncate">{s.email}</div>
                      <div className="col-span-2">
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${
                          s.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                          s.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-200 text-gray-600'
                        }`}>
                          {s.status}
                        </span>
                      </div>
                      <div className="col-span-3 text-xs text-[#34536b] truncate">
                        {(s.rechtsgebiete && s.rechtsgebiete.length) ? s.rechtsgebiete.join(', ') : '— alle —'}
                      </div>
                      <div className="col-span-2 text-xs text-[#34536b]">
                        {s.created_at ? new Date(s.created_at).toLocaleDateString('de-DE') : '—'}
                      </div>
                    </div>
                  ))}
                  {nlSubscribers.length === 0 && (
                    <p className="text-center py-10 text-[#34536b] text-sm">Noch keine Abonnenten</p>
                  )}
                </div>
              </div>
            ) : tab === 'sepa' ? (
              <div className="space-y-6" data-testid="admin-sepa-panel">
                <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                  <div className="px-5 py-3 bg-[#f5f8fa] flex items-center justify-between">
                    <h4 className="font-serif text-sm text-[#1B3A5C] tracking-wider inline-flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-[#b8893d]" /> SEPA-LASTSCHRIFTMANDATE
                    </h4>
                    <span className="text-xs text-[#34536b]">{sepaMandates.length} insgesamt</span>
                  </div>

                  {sepaLoading ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-6 h-6 text-[#b8893d] animate-spin mx-auto" />
                    </div>
                  ) : sepaMandates.length === 0 ? (
                    <p className="text-center py-12 text-[#34536b] text-sm" data-testid="sepa-empty">
                      Noch keine SEPA-Mandate eingegangen.
                    </p>
                  ) : (
                    <>
                      <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#f5f8fa] text-xs tracking-wider text-[#34536b] font-semibold border-t border-gray-100">
                        <div className="col-span-3">KONTOINHABER</div>
                        <div className="col-span-3">IBAN (MASKIERT)</div>
                        <div className="col-span-2">ZAHLUNGSART</div>
                        <div className="col-span-2">STATUS</div>
                        <div className="col-span-1">EINGANG</div>
                        <div className="col-span-1 text-right">AKTION</div>
                      </div>
                      {sepaMandates.map((m, i) => {
                        const statusCls = {
                          neu: 'bg-rose-100 text-rose-700',
                          geprueft: 'bg-amber-100 text-amber-700',
                          aktiv: 'bg-emerald-100 text-emerald-700',
                          widerrufen: 'bg-gray-200 text-gray-700',
                          ungueltig: 'bg-gray-200 text-gray-500',
                        }[m.status] || 'bg-rose-100 text-rose-700';
                        return (
                          <div
                            key={m.mandate_id}
                            className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-3.5 items-center hover:bg-[#fafaf7] ${i > 0 ? 'border-t border-gray-100' : ''}`}
                            data-testid={`sepa-row-${m.mandate_id}`}
                          >
                            <div className="col-span-3 min-w-0">
                              <div className="font-medium text-[#1B3A5C] truncate">{m.accountHolder}</div>
                              <div className="text-xs text-[#34536b] truncate">{m.email || '—'}</div>
                            </div>
                            <div className="col-span-3 text-xs font-mono text-[#1B3A5C] truncate" title={m.iban_masked}>
                              {m.iban_masked}
                            </div>
                            <div className="col-span-2 text-xs">
                              <span className={`px-2 py-0.5 rounded-sm ${
                                m.paymentType === 'wiederkehrend'
                                  ? 'bg-[#fbf7f0] text-[#b8893d]'
                                  : 'bg-gray-100 text-[#34536b]'
                              }`}>
                                {m.paymentType === 'wiederkehrend' ? 'Wiederkehrend' : 'Einmalig'}
                              </span>
                            </div>
                            <div className="col-span-2">
                              <select
                                value={m.status || 'neu'}
                                onChange={(e) => updateSepaStatus(m, e.target.value)}
                                data-testid={`sepa-status-${m.mandate_id}`}
                                className={`text-xs px-2 py-1 rounded-sm border-0 ${statusCls} focus:outline-none`}
                              >
                                <option value="neu">Neu</option>
                                <option value="geprueft">Geprüft</option>
                                <option value="aktiv">Aktiv</option>
                                <option value="widerrufen">Widerrufen</option>
                                <option value="ungueltig">Ungültig</option>
                              </select>
                            </div>
                            <div className="col-span-1 text-xs text-[#34536b]">
                              {m.created_at ? new Date(m.created_at).toLocaleDateString('de-DE') : '—'}
                            </div>
                            <div className="col-span-1 flex justify-end gap-1">
                              <PdfDownloadButton
                                endpoint={`/admin/sepa-mandate/${m.mandate_id}/pdf`}
                                filename={`SEPA-Mandat_${(m.accountHolder || 'Mandat').replace(/\s+/g, '_')}_${m.mandate_id}.pdf`}
                                variant="icon"
                                title="PDF herunterladen"
                                testid={`sepa-pdf-${m.mandate_id}`}
                              />
                              {isAdmin && <button
                                onClick={() => deleteSepa(m)}
                                data-testid={`sepa-delete-${m.mandate_id}`}
                                title="Löschen"
                                className="w-8 h-8 flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded-sm"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
                <p className="text-xs text-[#34536b]/70 px-1">
                  Aus Datenschutzgründen ist die vollständige IBAN nur im PDF-Export sichtbar.
                  Status &bdquo;Aktiv&ldquo; bestätigt, dass das Mandat geprüft und einsatzbereit ist.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#f5f8fa] text-xs tracking-wider text-[#34536b] font-semibold">
                  <div className="col-span-4">DATEINAME</div>
                  <div className="col-span-3">MANDANT · ZUORDNUNG</div>
                  <div className="col-span-2">GRÖSSE</div>
                  <div className="col-span-2">HOCHGELADEN</div>
                  <div className="col-span-1 text-right">AKTION</div>
                </div>
                {filteredDocs.map((d, i) => (
                  <div key={d.doc_id} className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 items-center hover:bg-[#fafaf7] ${i > 0 ? 'border-t border-gray-100' : ''}`}>
                    <div className="col-span-4 flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-[#b8893d] flex-shrink-0" />
                      <span className="text-sm text-[#1B3A5C] truncate">{d.filename}</span>
                    </div>
                    <div className="col-span-3 text-xs text-[#34536b] min-w-0">
                      <span className="block truncate">{d.owner_email || d.owner_name}</span>
                      <ZuordnungChip onDone={fetchAll} item={{ area: 'dokumente', area_label: 'Dokumente', id: d.doc_id, titel: d.filename, name: d.owner_name, email: d.owner_email, angabe_az: d.mandate_ref, zuordnung: d.zuordnung }} />
                    </div>
                    <div className="col-span-2 text-xs text-[#34536b]">{formatSize(d.size)}</div>
                    <div className="col-span-2 text-xs text-[#34536b]">{new Date(d.created_at).toLocaleDateString('de-DE')}</div>
                    <div className="col-span-1 flex justify-end gap-1">
                      <button onClick={() => downloadDoc(d)} className="w-8 h-8 flex items-center justify-center text-[#1B3A5C] hover:bg-[#f5f8fa] rounded-sm">
                        <Download className="w-4 h-4" />
                      </button>
                      {isAdmin && <button onClick={() => deleteDoc(d)} className="w-8 h-8 flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded-sm">
                        <Trash2 className="w-4 h-4" />
                      </button>}
                    </div>
                  </div>
                ))}
                {filteredDocs.length === 0 && <p className="text-center py-10 text-[#34536b] text-sm">Keine Dokumente gefunden</p>}
              </div>
            )}
          </div>
        </section>

        {/* Anfrage Detail Drawer */}
        {selectedAnfrage && (
          <div className="fixed inset-0 z-[80] flex" data-testid="anfrage-drawer">
            <div className="flex-1 bg-black/40" onClick={() => setSelectedAnfrage(null)} />
            <div className="w-full max-w-xl bg-white shadow-2xl overflow-y-auto border-l-4 border-[#b8893d]">
              <div className="bg-[#1B3A5C] text-white px-6 py-5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[#e0b56a] text-[10px] tracking-[0.3em] mb-2">ANFRAGE-DETAILS</div>
                  <h3 className="font-serif text-xl truncate">{selectedAnfrage.firstName} {selectedAnfrage.lastName}</h3>
                  <p className="text-white/60 text-xs mt-1">ID: {selectedAnfrage.anfrage_id}</p>
                </div>
                <button onClick={() => setSelectedAnfrage(null)} className="text-white/70 hover:text-white" data-testid="anfrage-drawer-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-6 space-y-5">
                <div>
                  <div className="text-[#34536b] text-[11px] tracking-wider mb-2">STATUS ÄNDERN</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(STATUS_BADGES).map(([key, b]) => (
                      <button
                        key={key}
                        onClick={() => updateAnfrageStatus(selectedAnfrage, key)}
                        data-testid={`anfrage-status-${key}`}
                        className={`text-xs px-3 py-1.5 rounded-sm transition-all ${selectedAnfrage.status === key ? `${b.cls} ring-2 ring-offset-1 ring-[#b8893d]` : 'bg-gray-100 text-[#34536b] hover:bg-gray-200'}`}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 bg-[#f5f8fa] p-4 rounded-sm">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-[#b8893d]" />
                    <a href={`mailto:${selectedAnfrage.email}`} className="text-[#1B3A5C] hover:underline">{selectedAnfrage.email}</a>
                  </div>
                  {selectedAnfrage.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-[#b8893d]" />
                      <a href={`tel:${selectedAnfrage.phone}`} className="text-[#1B3A5C] hover:underline">{selectedAnfrage.phone}</a>
                    </div>
                  )}
                  {selectedAnfrage.rechtsgebiet && (
                    <div className="text-xs text-[#34536b] mt-2">
                      Rechtsgebiet: <span className="text-[#1B3A5C] font-medium">{selectedAnfrage.rechtsgebiet}</span>
                    </div>
                  )}
                </div>

                <button type="button" onClick={() => { setErfassungId(selectedAnfrage.anfrage_id); setSelectedAnfrage(null); }} data-testid="anfrage-erfassungsbogen-oeffnen"
                  className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-[10px] text-xs tracking-wider bg-[#1B3A5C] hover:bg-[#15304d] text-white transition-colors duration-200">
                  <FileText className="w-4 h-4 text-[#e0b56a]" /> VOLLSTÄNDIGEN ERFASSUNGSBOGEN MIT DOKUMENTEN ANSEHEN
                </button>

                <div className="text-sm space-y-1.5">
                  <div className="text-[#34536b] text-[11px] tracking-wider mb-1">PERSÖNLICHE DATEN</div>
                  <div><span className="text-[#34536b]">Typ:</span> <span className="text-[#1B3A5C]">{selectedAnfrage.type === 'privat' ? 'Privatperson' : 'Unternehmen'}</span></div>
                  {selectedAnfrage.companyName && <div><span className="text-[#34536b]">Firma:</span> <span className="text-[#1B3A5C]">{selectedAnfrage.companyName}</span></div>}
                  <div><span className="text-[#34536b]">Anschrift:</span> <span className="text-[#1B3A5C]">{selectedAnfrage.street}, {selectedAnfrage.zip} {selectedAnfrage.city}, {selectedAnfrage.country}</span></div>
                  {selectedAnfrage.birthDate && <div><span className="text-[#34536b]">Geburtsdatum:</span> <span className="text-[#1B3A5C]">{selectedAnfrage.birthDate}</span></div>}
                  {selectedAnfrage.taxId && <div><span className="text-[#34536b]">Steuer-ID:</span> <span className="text-[#1B3A5C]">{selectedAnfrage.taxId}</span></div>}
                </div>

                <div>
                  <div className="text-[#34536b] text-[11px] tracking-wider mb-2 flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> ANLIEGEN</div>
                  <div className="bg-[#fbf7f0] border-l-2 border-[#b8893d] p-4 text-sm text-[#1B3A5C] whitespace-pre-wrap">
                    {selectedAnfrage.description || '—'}
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-4 grid grid-cols-2 gap-3 text-xs">
                  <div className="flex items-center gap-2">
                    {selectedAnfrage.email_sent ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Clock className="w-3.5 h-3.5 text-amber-500" />}
                    <span className="text-[#34536b]">Kanzlei-Mail: {selectedAnfrage.email_sent ? 'gesendet' : 'ausstehend'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedAnfrage.autoreply_sent ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Clock className="w-3.5 h-3.5 text-amber-500" />}
                    <span className="text-[#34536b]">Eingangsbestätigung: {selectedAnfrage.autoreply_sent ? 'gesendet' : 'ausstehend'}</span>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-4 flex justify-between items-center">
                  <span className="text-xs text-[#9aa5b1]">Eingegangen: {new Date(selectedAnfrage.created_at).toLocaleString('de-DE')}</span>
                  {isAdmin && <Button
                    onClick={() => deleteAnfrage(selectedAnfrage)}
                    variant="outline"
                    className="border-rose-300 text-rose-600 hover:bg-rose-50 text-xs"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> LÖSCHEN
                  </Button>}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
      <ChatWidget />
      <BackToTop />
      {pwUser && <PasswordDialog user={pwUser} isSelf={pwUser.user_id === currentUser?.user_id} onClose={() => setPwUser(null)} onDone={fetchAll} />}
      {erfassungId && <ErfassungDetailDialog anfrageId={erfassungId} onClose={() => setErfassungId(null)} />}
      <Toaster />
    </div>
  );
};

export default Admin;
